import { Button, Card, Flex, Form, Input, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { type Customer, type ServingTool } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { useMutation } from '../../../jsonapi-provider';

const SUCCESS_ADD_MESSAGE = 'Serving Tool added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Serving Tool updated successfully';

interface ServingToolFormProps {
  servingTool: ServingTool;
  onSuccess: (servingTool: ServingTool) => void;
  onCancel: () => void;
}

const ServingToolForm = (props: ServingToolFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { servingTool, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!servingTool.id, [servingTool.id]);
  const initialValues = useMemo(
    () => ({
      ...servingTool,
    }),
    [servingTool]
  );
  const [mutateServingTool, servingTools] = useMutation<Customer>(
    servingTool?.id ? ['serving-tool', servingTool.id] : ['serving-tool']
  );

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit = (submitted: ServingTool & Record<string, unknown>): void => {
    const servingToolToSubmit = { ...submitted } satisfies ServingTool;
    from(mutateServingTool(servingToolToSubmit))
      .pipe(
        switchMap((res) =>
          res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
        ),
        tap(() => {
          notification.success({
            message: ' ',
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
          });
        }),
        tap(onSuccess),
        catchError((error: Error) => {
          notification.error({
            message: '',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="serving-tool-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? servingTool.name : 'New Serving Tool'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button
                block
                htmlType="submit"
                type="primary"
                disabled={servingTools.isLoading}
                loading={servingTools.isLoading}
              >
                Save
              </Button>
              <Button block onClick={onCancel} disabled={servingTools.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default ServingToolForm;
